import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import "../styles/global.css";
import "./styles/Eventday.css";

/* Category colours mapped to project design tokens */
const CATEGORY_COLORS = {
  "Health Camp": "var(--bc-primary-d)",
  Workshop: "var(--color-indigo)",
  Wellness: "var(--color-warning)",
  Talk: "var(--color-secondary)",
  Appointment: "var(--color-danger-rose)",
  default: "var(--color-text-slate-500)",
};
const colorOf = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS.default;

const fmtDayHeading = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};
const timeRange = (s, e) => (e ? `${fmt12(s)} – ${fmt12(e)}` : fmt12(s));

/* First uploaded image for this event, or null if none.
   Backend stores `images` as a comma-separated filename string
   (same convention as doctor/clinic images), physically saved
   under public/event/. */
const firstEventImage = (e) => {
  if (!e?.images) return null;
  const first = e.images.split(",").map((s) => s.trim()).filter(Boolean)[0];
  return first ? `${API_BASE_URL}public/event/${first}` : null;
};

const EventDay = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    setLoading(true);
    // Public feed: only Active events, served by EventController::calendar
    fetch(`${API_BASE_URL}api/events`, {
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const list = data && data.status && Array.isArray(data.data) ? data.data : [];
        setEvents(list);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [token]);

  const categories = useMemo(() => {
    const set = new Set(events.map((e) => e.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [events]);

  const filtered = useMemo(() => {
    let list = events;
    if (activeCategory !== "All") list = list.filter((e) => e.category === activeCategory);
    if (filterText.trim()) {
      const ft = filterText.toLowerCase();
      list = list.filter(
        (e) =>
          e.title?.toLowerCase().includes(ft) ||
          e.location?.toLowerCase().includes(ft) ||
          e.speaker?.toLowerCase().includes(ft) ||
          e.description?.toLowerCase().includes(ft)
      );
    }
    return list;
  }, [events, activeCategory, filterText]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((e) => {
      const key = e.date || "Unscheduled";
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return Object.keys(map).sort((a, b) => a.localeCompare(b)).map((date) => ({ date, items: map[date] }));
  }, [filtered]);

  return (
    <>
      <Header />

      <main className="evd-page">
        <section className="evd-hero mb-0">
          <div className="container">
            <h1 className="text-white">Event days</h1>
            <p className="text-white">Camps, workshops, and wellness sessions — open to everyone. Find a day that works for you and just walk in.</p>
          </div>
        </section>
{/* Breadcrumb */}
      <section className="cl-breadcrumb-bar py-xl-3 py-3 mb-3">
        <div className="container-fluid">
          <nav aria-label="breadcrumb">
           <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/">
                  <i className="fa-solid fa-house me-1"></i> Home
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Event Day
              </li>
            </ol>
          </nav>
        </div>
      </section>
        <div className="container">
          <div className="evd-toolbar">
            <div className="evd-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                type="text"
                placeholder="Search events, speakers, or locations…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
            <div className="evd-chips">
              {categories.map((cat) => (
                <button
                  type="button"
                  key={cat}
                  className={`evd-chip ${activeCategory === cat ? "is-active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="evd-state">
              <div className="evd-spinner" />
              <p>Loading events…</p>
            </div>
          ) : grouped.length === 0 ? (
            <div className="evd-state">
              <i className="fa-solid fa-calendar-xmark evd-state-icon" />
              <p>No events match your search</p>
              <span>Try a different keyword or clear the category filter.</span>
            </div>
          ) : (
            <div className="evd-schedule">
              {grouped.map(({ date, items }) => (
                <div className="evd-day" key={date}>
                  <div className="evd-day-head">
                    <h2>{fmtDayHeading(date)}</h2>
                    <span className="evd-day-count">{items.length} event{items.length > 1 ? "s" : ""}</span>
                  </div>

                  <div className="evd-grid">
                    {items.map((e) => {
                      const thumb = firstEventImage(e);
                      return (
                        <article className="evd-card" key={e.id}>
                          {thumb ? (
                            <div className="evd-card-media evd-card-media-photo">
                              <img
                                src={thumb}
                                alt={e.title || "Event"}
                                loading="lazy"
                                onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                              />
                              {e.category && <span className="evd-card-tag">{e.category}</span>}
                            </div>
                          ) : (
                            <div className="evd-card-media" style={{ background: colorOf(e.category) }}>
                              <i className="fa-solid fa-heart-pulse" />
                              {e.category && <span className="evd-card-tag">{e.category}</span>}
                            </div>
                          )}
                          <div className="evd-card-body">
                            <div className="evd-card-time">
                              <i className="fa-solid fa-clock" />
                              {timeRange(e.start_time, e.end_time)}
                            </div>
                            <h3>{e.title}</h3>
                            {e.description && <p className="evd-card-desc">{e.description}</p>}
                            <div className="evd-card-meta">
                              {e.location && (
                                <span><i className="fa-solid fa-location-dot" />{e.location}</span>
                              )}
                              {e.speaker && (
                                <span><i className="fa-solid fa-user" />{e.speaker}</span>
                              )}
                            </div>
                            <Link to={`/eventDetail/${e.id}`} >
                               Learn More<i className="fa-solid fa-arrow-right" />
                            </Link>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default EventDay;